import clsx from 'clsx'
import React, { CSSProperties, FC, useCallback, useRef, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import { Subscribed } from '@Components/CMS/NotifyMe/Subscribed'
import { SubscribeNewsletterForm } from '@Components/SubscribeNewsletterForm'
import { gtm } from '@Globals/Google/DataLayer'
import { useNewsletterSubscription } from '@Globals/Newsletter/useNewsletterSubscription'
import { useTranslation } from '@Globals/Translations'
import { useScrollTo } from '@Globals/useScrollTo'

import styles from './NotifyMe.module.scss'

export interface NotifyMeProps {
  backgroundColor?: string
  description?: string
  fillContainer?: boolean
  id?: string
  identifyingLabel: string
  isInModal?: boolean
  submitText: string
  thankYouText: string
  title?: string
}

export const NotifyMe: FC<NotifyMeProps> = ({
  backgroundColor,
  description,
  fillContainer = false,
  id,
  identifyingLabel,
  isInModal,
  submitText,
  thankYouText,
  title,
}) => {
  const [formSubmitted, setFormSubmitted] = useState(false)
  const { subscribe } = useNewsletterSubscription()
  const { t } = useTranslation()
  const scrollToRef = useRef<HTMLDivElement | null>(null)
  const scrollToSubscribeBlock = useScrollTo(scrollToRef)

  const onSubscribe = useCallback(async (email: string) => {
    const result = await subscribe({ campaignLabel: identifyingLabel, email, source: 'cms_notify_me' })
    setFormSubmitted(result)
    scrollToSubscribeBlock()

    gtm({
      event: 'notifyMeCms',
      moduleKey: identifyingLabel,
    })

    ampli.cmsNotifyMeOptIn({
      campaign_label: identifyingLabel,
    })
  }, [identifyingLabel, scrollToSubscribeBlock, subscribe])

  return (
    <div
      className={clsx(styles.notifyMe, fillContainer && styles.fillContainer)}
      ref={scrollToRef}
      style={{
        backgroundColor: isInModal ? 'white' : backgroundColor,
      } as CSSProperties}
    >
      {formSubmitted
        ? <Subscribed className={styles.subscribed} id={id} message={thankYouText} />
        : (
          <SubscribeNewsletterForm
            description={description}
            id={id}
            onSubscribe={onSubscribe}
            submitText={submitText || t('cms:NOTIFY_ME_VIA_EMAIL', 'Notify me')}
            title={title}
            variant="column"
          />
        )}
    </div>
  )
}
